import { useEffect, useRef, useState } from "react";
import { fetchShiftSummary, DataReceiverError } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Shift Summary modal.
 *
 * ── React 18 Strict Mode behaviour ───────────────────────────────────────────
 *
 * In development, React 18 Strict Mode deliberately runs each effect twice
 * (mount → cleanup → mount) to surface bugs. This means:
 *
 *   1. Effect fires  → fetch starts, hasFetched = true
 *   2. Cleanup fires → controller.abort() → request cancelled (status 0)
 *   3. Effect fires  → hasFetched is true → blocked ✓ (no second request)
 *
 * The abort in step 2 is the problem: the ref blocks the redundant second
 * fetch correctly, but the first fetch is already dead.
 *
 * Fix: replace AbortController with an `isMounted` ref. The async callback
 * checks `isMounted.current` before calling setState — so if the component
 * truly unmounts mid-fetch, state updates are suppressed without aborting the
 * in-flight request. Strict Mode's fake cleanup sets isMounted = false then
 * immediately back to true on re-mount, so the response from the first
 * (already-in-flight) fetch is still applied correctly.
 *
 * This is the same pattern used by useBuildingProduction.js in this codebase.
 * The trade-off is that a genuine unmount (user closes the modal) does not
 * cancel the network request — but the response is simply discarded when it
 * arrives, which is the correct outcome for a modal that's already gone.
 *
 * ── Caching ───────────────────────────────────────────────────────────────────
 *
 * Results are cached per-employee for CACHE_TTL_MS (60 s) in a module-level
 * Map. Re-opening the modal for the same employee within the TTL window returns
 * the cached result instantly with no network call or loading flash.
 *
 * @param {string | null | undefined} employeeNumber
 *   The employee's EmpID string (e.g. "101026" or "C100218").
 *   Pass null / undefined to skip the fetch (modal not yet open).
 *
 * @returns {{
 *   shiftSummary: {
 *     shiftStart:            string | null,
 *     shiftEnd:              string | null,
 *     totalActiveTime:       string,
 *     totalIdleTime:         string,
 *     jobsCompleted:         number,
 *     jobsInProgress:        number,
 *     stationsVisited:       string[],
 *   } | null,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

// ── Module-level per-employee cache ───────────────────────────────────────────
//
// Map<employeeNumber, { data: ShiftSummary, fetchedAt: number }>
// Stale entries are evicted on next access (getCached returns null).

const _cache = new Map();
const CACHE_TTL_MS = 60_000; // 1 minute

function getCached(employeeNumber) {
  const entry = _cache.get(employeeNumber);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    _cache.delete(employeeNumber);
    return null;
  }
  return entry;
}

export function useShiftSummary(employeeNumber) {
  const [shiftSummary, setShiftSummary] = useState(() => {
    const cached = employeeNumber ? getCached(employeeNumber) : null;
    return cached?.data ?? null;
  });

  const [loading, setLoading] = useState(() => {
    if (!employeeNumber) return false;
    return !getCached(employeeNumber);
  });

  const [error, setError] = useState(null);

  // hasFetched: blocks the redundant third effect invocation that Strict Mode
  // would otherwise trigger (mount → cleanup → mount means the effect body
  // runs twice; we only want one fetch per employeeNumber).
  const hasFetched = useRef(false);
  const prevEmployeeNumber = useRef(null);

  // isMounted: suppresses setState calls after a genuine unmount without
  // aborting the in-flight request (see header comment above).
  const isMounted = useRef(true);

  // Reset hasFetched when the selected employee changes so a new selection
  // always triggers a fresh fetch even though the modal stays mounted.
  if (prevEmployeeNumber.current !== employeeNumber) {
    hasFetched.current = false;
    prevEmployeeNumber.current = employeeNumber;
  }

  useEffect(() => {
    // Mark mounted on every effect invocation — covers the Strict Mode
    // re-mount after the fake cleanup sets isMounted to false.
    isMounted.current = true;

    // No employee selected — reset to idle and skip fetch.
    if (!employeeNumber?.trim()) {
      setShiftSummary(null);
      setLoading(false);
      setError(null);
      return () => {
        isMounted.current = false;
      };
    }

    // Serve from cache if the entry is still fresh — no network call needed.
    const cached = getCached(employeeNumber);
    if (cached) {
      setShiftSummary(cached.data);
      setLoading(false);
      setError(null);
      return () => {
        isMounted.current = false;
      };
    }

    // Guard against the second effect invocation from Strict Mode.
    // At this point the first invocation already fired a fetch that is still
    // in flight — let it land; don't fire a second one.
    if (hasFetched.current) {
      return () => {
        isMounted.current = false;
      };
    }
    hasFetched.current = true;

    console.log(
      "[useShiftSummary] effect fired, employeeNumber:",
      employeeNumber,
    );

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchShiftSummary(
          { employeeNumber: employeeNumber.trim() },
          // No AbortSignal — see header comment. isMounted guards setState instead.
        );

        // Cache before setState so any synchronous re-render triggered by
        // setState sees the cached value and skips a redundant fetch.
        _cache.set(employeeNumber, { data, fetchedAt: Date.now() });

        if (!isMounted.current) return; // modal closed while fetch was in flight
        setShiftSummary(data);
      } catch (err) {
        if (!isMounted.current) return;
        setError(
          err instanceof DataReceiverError
            ? err.message
            : "Unexpected error loading shift summary.",
        );
        setShiftSummary(null);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    })();

    return () => {
      console.log("[useShiftSummary] cleanup — aborting for:", employeeNumber);
      isMounted.current = false;
    };
  }, [employeeNumber]);

  return { shiftSummary, loading, error };
}
